import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, FlaskConical, XCircle } from "lucide-react";
import { getDatasets } from "../lib/api";
import { DataTable } from "../components/DataTable";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { num } from "../lib/format";

const KIND_LABEL = {
  primary_operational: "Primary operational system",
  peer_benchmark: "Peer benchmark",
  need_indicator: "Need indicator",
  national_benchmark: "National benchmark",
  canonical_entity: "Canonical entity master",
  placeholder: "Placeholder",
};

export default function DatasetsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    getDatasets().then(setData).catch(setErr);
  }, []);

  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading label="Reading the catalogue" />;

  const real = data.datasets.filter((d) => d.provenance === "real");
  const seeded = data.datasets.filter((d) => d.provenance === "seeded_placeholder");
  const candidate = data.datasets.filter((d) => d.provenance === "candidate");
  const shown =
    filter === "all"
      ? data.datasets
      : filter === "real"
        ? real
        : filter === "seeded"
          ? seeded
          : candidate;

  return (
    <div className="max-w-[1500px]">
      <PageHeader overline="Data sources" title="Every dataset behind an answer" testId="datasets-header">
        Who published each dataset, what it carries, and what it cannot support.
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="datasets-summary">
        <Tile label="Datasets registered" value={data.datasets.length} />
        <Tile label="Datasets in use" value={real.length} accent="emerald" />
        <Tile label="Placeholder layers (capped Directional)" value={seeded.length} accent="amber" />
        <Tile label="State-indicator rows" value={num(data.counts.ref_indicator)} />
      </div>

      <div className="flex gap-1.5 mb-4" data-testid="datasets-filter">
        {[
          { id: "all", label: `All (${data.datasets.length})` },
          { id: "real", label: `In use (${real.length})` },
          { id: "seeded", label: `Placeholder (${seeded.length})` },
          { id: "candidate", label: `Declined (${candidate.length})` },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`datasets-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              filter === f.id ? "bg-[#760D28] text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-300"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {shown.map((d) => (
          <article
            key={d.id}
            data-testid={`dataset-card-${d.id}`}
            className="bg-white border border-stone-200 rounded-2xl p-7 hover:border-stone-300 transition-colors"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12px] font-semibold text-[#A01236]">
                    {KIND_LABEL[d.kind] || d.kind}
                  </span>
                  {d.provenance === "real" ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                      <CheckCircle2 className="w-3 h-3" /> in use
                    </span>
                  ) : d.provenance === "candidate" ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-stone-600 bg-stone-100 border border-stone-200 px-1.5 py-0.5 rounded">
                      <XCircle className="w-3 h-3" /> declined for v1
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                      <FlaskConical className="w-3 h-3" /> seeded placeholder
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg font-semibold mt-1 leading-snug">{d.name}</h3>
                <p className="text-xs text-stone-600 mt-0.5">{d.publisher}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="num text-lg text-stone-900">{num(d.record_count)}</div>
                <div className="text-[12px] text-stone-600">records held</div>
              </div>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 mt-4 text-xs">
              <Meta label="Granularity" value={d.granularity} />
              <Meta label="Reference period" value={d.reference_period} />
              <Meta label="Access path" value={d.access} />
            </dl>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Head tone="emerald">What it unlocks</Head>
                <ul className="mt-1.5 space-y-1.5">
                  {d.unlocks.map((u, i) => (
                    <li key={i} className="text-xs text-stone-700 leading-relaxed flex gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{u}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <Head tone="rose">What it cannot support</Head>
                <ul className="mt-1.5 space-y-1.5">
                  {d.cannot_support.map((u, i) => (
                    <li key={i} className="text-xs text-stone-700 leading-relaxed flex gap-2">
                      <XCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0" />
                      <span>{u}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {d.replace_with && (
              <p className="text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-5 leading-relaxed">
                <span className="font-semibold">Replace with:</span> {d.replace_with}
              </p>
            )}
            {d.decision && (
              <p className="text-[13px] text-stone-700 bg-stone-50 border border-stone-200 rounded-lg px-4 py-3 mt-5 leading-relaxed">
                <span className="font-semibold">Decision:</span> {d.decision}
              </p>
            )}

            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-stone-100 flex-wrap">
              <span className="num text-[12px] text-stone-600">
                collections: {Object.entries(d.collection_counts).map(([k, v]) => `${k}(${num(v)})`).join(" · ") || "—"}
              </span>
              {d.endpoint && (
                <a href={d.endpoint} target="_blank" rel="noreferrer"
                   data-testid={`dataset-link-${d.id}`}
                   className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#A01236] hover:underline">
                  {d.endpoint} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      <section className="mt-8" data-testid="indicator-inventory">
        <h2 className="font-display text-xl font-semibold tracking-tight">
          State indicator inventory
        </h2>
        <p className="text-sm text-stone-600 mt-1 mb-3 max-w-3xl leading-relaxed">
          Every published indicator loaded into the need-vs-funding index, with its direction of
          need, coverage and observed range. State names in the source files are normalised to the
          LGD state master on ingest; aggregate rows such as “India” and “Other States” are dropped.
        </p>
        <DataTable
          testId="indicator-table"
          rows={data.indicators}
          columns={[
            { key: "label", label: "Indicator", render: (r) => (
              <div>
                <div className="font-medium text-stone-900">{r.label}</div>
                <div className="num text-[12px] text-stone-600">{r.indicator_key}</div>
              </div>
            )},
            { key: "unit", label: "Unit" },
            { key: "direction_note", label: "Direction of need" },
            { key: "reference_period", label: "Period", align: "right" },
            { key: "states_covered", label: "States", align: "right" },
            { key: "min", label: "Min", align: "right" },
            { key: "mean", label: "Mean", align: "right" },
            { key: "max", label: "Max", align: "right" },
            { key: "themes", label: "Themes", render: (r) => r.themes.join(", ") },
          ]}
        />
      </section>

      {data.national_indicators?.length > 0 && (
        <section className="mt-6" data-testid="national-indicators">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            National-only reference (not usable for targeting)
          </h2>
          <p className="text-sm text-stone-600 mt-1 mb-3 max-w-3xl leading-relaxed">
            Gross Enrolment Ratios arrive with no geography column at all, so they sit outside the
            index and are used only as national context in an education answer.
          </p>
          <DataTable
            testId="national-table"
            rows={data.national_indicators}
            columns={[
              { key: "level", label: "Education level" },
              { key: "value", label: "Gross enrolment ratio", align: "right" },
              { key: "year", label: "Year", align: "right" },
            ]}
          />
        </section>
      )}

      <section className="mt-8" data-testid="provenance-legend">
        <h2 className="font-display text-lg font-semibold tracking-tight">Provenance legend</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {Object.entries(data.provenance_legend).map(([k, v]) => (
            <div key={k} className="bg-white border border-stone-200 rounded-lg p-4">
              <div className="num text-xs text-stone-900">{k}</div>
              <p className="text-xs text-stone-600 mt-1 leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const Tile = ({ label, value, accent }) => (
  <div className={`bg-white border rounded-xl px-4 py-3 ${
    accent === "emerald" ? "border-emerald-200" : accent === "amber" ? "border-amber-200" : "border-stone-200"}`}>
    <div className="text-[12px] text-stone-600">{label}</div>
    <div className="num text-xl font-medium text-stone-900 mt-0.5">{value}</div>
  </div>
);

const Meta = ({ label, value }) => (
  <div>
    <dt className="text-[12px] text-stone-600">{label}</dt>
    <dd className="text-stone-800 mt-0.5 leading-relaxed">{value || "—"}</dd>
  </div>
);

const Head = ({ children, tone }) => (
  <div className={`text-[12px] font-semibold ${
    tone === "emerald" ? "text-emerald-700" : "text-rose-700"}`}>
    {children}
  </div>
);
